'use client';
import React, { Suspense } from 'react';

import { useSearchParams } from 'next/navigation';
import * as Styled from './styled';
import { Card, Row, Stack } from '@/ui/primatives';
import CardList from '@/ui/CardList';

import { IRepository, IRepositoryFile, IRepositoryEntry } from '@/types';

import { TDownloadProgress } from '@/lib/repositoryFileApi';

import useRepositoryFile from '@/hooks/useRepositoryFile';

import TextFileView from './TextFileView';
import ImageFileView from './ImageFileView';
import MediaFileView from './MediaFileView';
import PdfFileView from './PdfFileView';

import TextFileLoader from './TextFileLoader';
import DownloadProgressIndicator from '@/components/DownloadProgressIndicator';

import ErrorBoundary from '@/components/ErrorBoundary';
import { SuspenseLoader } from '@/components/OxenLoader';
import * as format from '@/lib/format';
import DebugJson from '@/ui/DebugJson';

const MAX_FILE_SIZE = 100000000;

const AsyncFileRenderer: React.FC<{ entry: IRepositoryEntry; file?: IRepositoryFile }> = ({ entry, file }) => {
  if (file?.data_type === 'text') {
    return <TextFileView entry={entry} file={file} />; // Text, Markdown, JSON
  }
  return <div>Unknown content type</div>;
};

const AsyncFileView: React.FC<{ repository: IRepository; entry: IRepositoryEntry; fallback?: React.ReactNode }> = ({
  repository,
  entry,
  fallback = null,
}) => {
  const [progress, setProgress] = React.useState<TDownloadProgress | undefined>();
  const searchParams = useSearchParams();
  const fileData = useRepositoryFile({
    repository: repository,
    resource: entry?.resource,
    filename: entry?.filename,
    dataType: entry?.data_type || 'unknown',
    mimeType: entry?.mime_type || 'unknown',
    query: searchParams as any,
    onProgress: setProgress,
  });

  return fileData.isLoading || fileData.status === 'pending' ? (
    <>
      {progress && <DownloadProgressIndicator progress={progress} />}
      {fallback}
    </>
  ) : (
    <>
      <DebugJson rootNodeName="fileData" json={fileData} />
      <AsyncFileRenderer entry={entry} file={fileData.file} />
    </>
  );
};

const OversizeFileCard: React.FC<{ entry: IRepositoryEntry }> = ({ entry }) => (
  <Card>
    <div>
      File too large to preview. This file is {format.humanFileSize(entry.size)}, Oxenhub will display files with a
      maximum size of {format.humanFileSize(MAX_FILE_SIZE)}. You can download the file to view it locally.
    </div>
  </Card>
);

const FileRenderer: React.FC<{ repository: IRepository; entry: IRepositoryEntry; fileUrl?: string }> = ({
  repository,
  entry,
  fileUrl,
}) => {
  if (!entry) {
    return <div>No entry</div>;
  }

  if (entry?.size && entry?.size > MAX_FILE_SIZE) {
    return <OversizeFileCard entry={entry} />;
  }

  if (entry.mime_type === 'application/pdf') {
    return <PdfFileView fileUrl={fileUrl} />;
  }

  switch (entry.data_type) {
    case 'image':
      return (
        <Stack gap={20}>
          <Row alignItems="center" gap={50} justifyContent="space-between">
            <div>File Size: {format.humanFileSize(entry?.size)}</div>
            <div>{entry.mime_type}</div>
          </Row>
          <ImageFileView repository={repository} entry={entry} fileUrl={fileUrl} />
        </Stack>
      );
    case 'video':
    case 'audio':
      return <MediaFileView repository={repository} entry={entry} fileUrl={fileUrl} />;
    default:
      return <AsyncFileView repository={repository} entry={entry} fallback={<TextFileLoader rows={20} />} />;
  }
};

const FileView: React.FC<{
  repository: IRepository;
  entry: IRepositoryEntry;
  fileUrl?: string;
  children?: React.ReactNode;
}> = ({ repository, entry, fileUrl, children }) => (
  <div>
    <ErrorBoundary fallback={<div>FileView Error</div>} containerComponent="FileView">
      <CardList compressed>
        {children}
        <Styled.FileCard>
          <DebugJson rootNodeName="entry" json={entry} />
          <Suspense
            key={`repo-file-${entry?.resource?.version}`}
            fallback={<SuspenseLoader>Loading file...</SuspenseLoader>}
          >
            <FileRenderer repository={repository} entry={entry} fileUrl={fileUrl} />
          </Suspense>
        </Styled.FileCard>
      </CardList>
    </ErrorBoundary>
  </div>
);

export default FileView;
